import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type KeyboardEvent,
} from "react";
import styled from "styled-components";
import {
  defaultRangeExtractor,
  measureElement,
  observeElementRect,
  useVirtualizer,
  type Range,
} from "@tanstack/react-virtual";
import { Box, Button, Flex } from "orcs-design-system";

import InboxEmpty from "./InboxEmpty";
import { inboxKind, type InboxItem } from "./registry";

/** space.xs. Same as the old Flex gap. */
const CARD_GAP_PX = 2;
const CARD_ESTIMATE_PX = 112;
// shortcut: 640px flyout default, Jest ResizeObserver is a no-op
const LIST_INITIAL_RECT = { width: 380, height: 640 };

const VirtualRow = styled(Box)<{ $start: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  transform: translateY(${(props) => props.$start}px);
`;

const InboxList = ({
  items,
  selectedKey,
  arrivedKey = null,
  otherCount,
  hasMore,
  loadingMore,
  onSelect,
  onLoadMore,
}: {
  items: InboxItem[];
  selectedKey: string | null;
  /** Key the auto-advance just opened; only that card gets the arrive pulse. */
  arrivedKey?: string | null;
  otherCount: number;
  hasMore: boolean;
  loadingMore: boolean;
  onSelect: (key: string) => void;
  onLoadMore: () => void;
}) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const pendingFocusKey = useRef<string | null>(null);
  const selectedIndex = selectedKey
    ? items.findIndex((item) => item.key === selectedKey)
    : -1;

  const rangeExtractor = useCallback(
    (range: Range) => {
      const indexes = defaultRangeExtractor(range);
      if (selectedIndex >= 0 && !indexes.includes(selectedIndex)) {
        return [...indexes, selectedIndex].sort((a, b) => a - b);
      }
      return indexes;
    },
    [selectedIndex]
  );

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => CARD_ESTIMATE_PX,
    overscan: 8,
    gap: CARD_GAP_PX,
    getItemKey: (index) => items[index]?.key ?? index,
    initialRect: LIST_INITIAL_RECT,
    rangeExtractor,
    observeElementRect: (instance, cb) =>
      observeElementRect(instance, (rect) => {
        cb(
          rect.height < 1
            ? {
                width: Math.max(rect.width, LIST_INITIAL_RECT.width),
                height: LIST_INITIAL_RECT.height,
              }
            : rect
        );
      }),
    measureElement: (element, entry, instance) => {
      const size = measureElement(element, entry, instance);
      return size < 1 ? CARD_ESTIMATE_PX : size;
    },
  });

  useEffect(() => {
    if (selectedIndex < 0) {
      return;
    }
    virtualizer.scrollToIndex(selectedIndex, { align: "auto" });
  }, [selectedIndex, virtualizer]);

  useLayoutEffect(() => {
    const key = pendingFocusKey.current;
    if (!key) {
      return;
    }
    const node = parentRef.current?.querySelector(
      `[data-testid="inbox-card-${CSS.escape(key)}"]`
    );
    if (!(node instanceof HTMLElement)) {
      return;
    }
    pendingFocusKey.current = null;
    node.focus();
  }, [selectedKey]);

  const onListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
      return;
    }
    if (items.length === 0) {
      return;
    }
    event.preventDefault();
    let next = 0;
    if (selectedIndex >= 0 && event.key === "ArrowDown") {
      next = Math.min(items.length - 1, selectedIndex + 1);
    } else if (selectedIndex >= 0) {
      next = Math.max(0, selectedIndex - 1);
    }
    const key = items[next]?.key;
    if (!key || key === selectedKey) {
      return;
    }
    pendingFocusKey.current = key;
    onSelect(key);
  };

  return (
    <Flex
      flexDirection="column"
      gap="s"
      width="100%"
      height="100%"
      minHeight={0}
    >
      {items.length === 0 && !hasMore ? (
        <InboxEmpty otherCount={otherCount} />
      ) : null}
      <Box
        ref={parentRef}
        flex="1 1 auto"
        minHeight={0}
        overflow="auto"
        onKeyDown={onListKeyDown}
      >
        <Box
          role="list"
          aria-label="Inbox items"
          position="relative"
          width="100%"
          height={`${virtualizer.getTotalSize()}px`}
        >
          {virtualizer.getVirtualItems().map((row) => {
            const item = items[row.index];
            if (!item) {
              return null;
            }
            const { Card } = inboxKind(item.kind);
            return (
              <VirtualRow
                key={item.key}
                ref={virtualizer.measureElement}
                data-index={row.index}
                $start={row.start}
                role="listitem"
              >
                <Card
                  item={item}
                  selected={item.key === selectedKey}
                  arrived={item.key === arrivedKey}
                  onSelect={onSelect}
                />
              </VirtualRow>
            );
          })}
        </Box>
        {hasMore && items.length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            disabled={loadingMore}
            onClick={onLoadMore}
          >
            Load more
          </Button>
        ) : null}
      </Box>
    </Flex>
  );
};

export default InboxList;
